import React, { useState, useEffect } from 'react';
import { Sparkles, Rocket } from 'lucide-react';
import Spline from '@splinetool/react-spline';

interface LoadingAnimationProps {
  message: string;
}

export function LoadingAnimation({ message }: LoadingAnimationProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [splineLoaded, setSplineLoaded] = useState(false);
  
  const steps = [
    "Analizando tu CV...",
    "Identificando habilidades clave...",
    "Analizando requisitos del empleo...",
    "Comparando perfil con requisitos...",
    "Optimizando experiencia laboral...",
    "Mejorando habilidades destacadas...",
    "Generando carta de presentación...",
    "Preparando análisis de mejoras...",
    "Aplicando formato profesional...",
    "Realizando últimos ajustes..."
  ];
  
  // Efecto para cambiar el mensaje cada cierto tiempo
  useEffect(() => {
    // Ajustamos los tiempos para que coincidan mejor con el proceso real
    // El proceso completo toma aproximadamente 30 segundos
    const stepIntervals = [
      2000,  // Analizando tu CV: 2 segundos
      4000,  // Identificando habilidades: 2 segundos después
      6000,  // Analizando requisitos: 2 segundos después
      8000,  // Comparando perfil: 2 segundos después
      11000, // Optimizando experiencia: 3 segundos después
      14000, // Mejorando habilidades: 3 segundos después
      17000, // Generando carta: 3 segundos después
      20000, // Preparando análisis: 3 segundos después
      23000, // Aplicando formato: 3 segundos después
      // El último paso (Realizando últimos ajustes) se mostrará durante los 7 segundos restantes
    ];
    
    // Configuramos temporizadores para cada paso
    const timers = stepIntervals.map((delay, index) => {
      return setTimeout(() => {
        setCurrentStep(index + 1);
      }, delay);
    });
    
    return () => {
      // Limpiamos todos los temporizadores
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleSplineLoad = () => {
    setSplineLoaded(true);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-blue-900/70 via-indigo-900/70 to-purple-900/60 backdrop-blur-md rounded-2xl p-8 max-w-md w-full mx-4 border border-purple-500/30 shadow-[0_8px_30px_rgba(138,75,175,0.3)]">
        <div className="flex flex-col items-center text-center">
          {/* Contenedor principal con la animación de Spline */}
          <div className="relative w-64 h-64 mb-6 rounded-xl overflow-hidden">
            {/* Fondo brillante durante la carga */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-purple-600/30 animate-pulse rounded-xl"></div>
            
            {/* Modelo 3D de Spline */}
            <div className="absolute inset-0 rounded-xl overflow-hidden">
              <Spline 
                scene="https://prod.spline.design/3FSg2p2RZbTGqdt7/scene.splinecode" 
                onLoad={handleSplineLoad}
              />
            </div>
            
            {/* Elemento de carga mientras Spline se inicializa */}
            {!splineLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            
            {/* Efectos superpuestos para enfatizar la IA */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-purple-500/0"></div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-blue-500/0"></div>
          </div>
          
          {/* Título con énfasis en IA */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 animate-gradient-x">
                Creando CV con IA
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 blur-lg animate-pulse-slow"></span>
            </h2>
            <p className="text-purple-200 text-base mt-3">
              {steps[currentStep]}
            </p>
          </div>
          
          <p className="text-gray-300 text-sm mb-6">
            {message}
          </p>
          
          {/* Indicador de progreso */}
          <div className="w-full bg-gray-700/30 rounded-full h-1.5 mb-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${Math.min(100, (currentStep + 1) * 10)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-400">
            Progreso: {Math.min(100, (currentStep + 1) * 10)}%
          </p>
        </div>
      </div>
    </div>
  );
}